import { useState, useCallback, type ReactNode } from 'react';
import { ViewTransitionContext } from './ViewTransitionContext';
import { type NavigateOptions, type To, useNavigate } from 'react-router-dom';

// // Define types for the navigation
// type NavigateOptions = {
//   viewTransition?: boolean;
//   state?: {
//     isBack?: boolean;
//     // biome-ignore lint/suspicious/noExplicitAny: <explanation>
//     [key: string]: any;
//   };
// };

type NavigateTo = number | string;

export type Navigate = (to: NavigateTo, options?: NavigateOptions) => void;

// Define the context type
export interface ViewTransitionContextType {
  navigateBack: (route?: string) => void;
  navigateNormal: (to: To, options: NavigateOptions | undefined) => void;
  isBackViewTransition: boolean;
}

interface ViewTransitionProviderProps {
  children: ReactNode;
}

/**
 * Provider component for view transitions
 */
export function ViewTransitionProvider({ 
  children, 
}: ViewTransitionProviderProps): JSX.Element {
  const [isBackViewTransition, setIsBackViewTransition] = useState<boolean>(false);
  const navigate = useNavigate();
  
  // Navigate function that can handle backward navigation with view transitions
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const navigateBack = useCallback<(route?: string) => void>((route?: string) => {
    setIsBackViewTransition(true);

    const to = route ?? -1;

    //@ts-expect-error It works
    navigate(to, {
      viewTransition: true,
      state: { isBack: true },
    });
    
    // Handle different navigation scenarios
    // if (typeof to === 'number') {
    //   // For relative navigation (e.g., -1 to go back)
    //   history.go(to);
    // } else {
    //   // For path-based navigation
    //   history.push(to, state);
    // }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const navigateNormal = useCallback<(to: To, options: NavigateOptions | undefined) => void>((to: To, options: NavigateOptions | undefined) => {
    setIsBackViewTransition(false);
    navigate(to, options);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Context value with navigate function and isBackViewTransition state
  const contextValue: ViewTransitionContextType = {
    navigateBack,
    navigateNormal,
    isBackViewTransition,
  };
  
  return (
    <ViewTransitionContext.Provider value={contextValue}>
      <div 
        className={isBackViewTransition ? 'view-transition-back' : 'view-transition'}
        style={{
          viewTransitionName: isBackViewTransition ? "back" : undefined,
        }}
      >
        {children}
      </div>
    </ViewTransitionContext.Provider>
  );
}